import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  Send as ApplicationIcon,
  Schedule as InterviewIcon,
  CheckCircle as OfferIcon,
  Work as JobIcon,
  Person as ProfileIcon,
  AutoFixHigh as AnswersIcon,
  Description as ResumeIcon,
  FolderOpen as ApplicationsIcon,
  BookmarkBorder as SavedIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { applicationsApi, profileApi } from '../../services/api';
import ExtensionStatusCard from '../../components/extension/ExtensionStatusCard';
import { ApplicationStats, ProfileSnapshot, statusMeta } from '../../types';
import { errorMessage } from '../../utils/errors';
import { formatPercent, formatRelative } from '../../utils/format';

interface StrengthItem {
  label: string;
  done: boolean;
  to: string;
}

function profileStrength(snapshot: ProfileSnapshot): StrengthItem[] {
  const p = snapshot.profile;
  return [
    { label: 'First and last name', done: Boolean(p.first_name && p.last_name), to: '/profile' },
    { label: 'Phone number', done: Boolean(p.phone), to: '/profile' },
    { label: 'Address', done: Boolean(p.address_line1 && p.city), to: '/profile' },
    { label: 'At least one education entry', done: snapshot.education.length > 0, to: '/profile' },
    { label: 'At least one work experience', done: snapshot.experience.length > 0, to: '/profile' },
    { label: 'A default resume', done: snapshot.resumes.some((r) => r.is_default), to: '/resumes' },
    {
      label: 'Work authorization answered',
      done: Boolean(snapshot.answers['eligibility.work_authorized']),
      to: '/answers',
    },
  ];
}

const DashboardPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [stats, setStats] = useState<ApplicationStats | null>(null);
  const [snapshot, setSnapshot] = useState<ProfileSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setStatsError(null);
    setSnapshotError(null);
    const [statsResult, snapshotResult] = await Promise.allSettled([
      applicationsApi.stats(),
      profileApi.snapshot(),
    ]);
    if (statsResult.status === 'fulfilled') setStats(statsResult.value);
    else setStatsError(errorMessage(statsResult.reason, 'Could not load application stats.'));
    if (snapshotResult.status === 'fulfilled') setSnapshot(snapshotResult.value);
    else setSnapshotError(errorMessage(snapshotResult.reason, 'Could not load your profile.'));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const strength = useMemo(() => (snapshot ? profileStrength(snapshot) : null), [snapshot]);
  const strengthPct = strength
    ? Math.round((strength.filter((s) => s.done).length / strength.length) * 100)
    : 0;

  const byStatus = stats?.by_status ?? {};
  const count = (...keys: string[]) =>
    keys.reduce((sum, k) => sum + (byStatus[k as keyof typeof byStatus] ?? 0), 0);

  const quickStats = stats
    ? [
        { label: 'Total applications', value: stats.total, icon: ApplicationsIcon, color: 'primary' },
        { label: 'Saved', value: count('saved', 'in_progress'), icon: SavedIcon, color: 'info' },
        { label: 'Applied (last 7 days)', value: stats.applied_last_7_days, icon: ApplicationIcon, color: 'secondary' },
        {
          label: 'In interviews',
          value: count('interview_scheduled', 'interviewed'),
          icon: InterviewIcon,
          color: 'warning',
        },
        { label: 'Offers', value: count('offer_received', 'offer_accepted'), icon: OfferIcon, color: 'success' },
      ]
    : [];

  const quickActions = [
    { label: 'Search jobs', to: '/', icon: JobIcon },
    { label: 'View applications', to: '/applications', icon: ApplicationsIcon },
    { label: 'Edit profile', to: '/profile', icon: ProfileIcon },
    { label: 'Answer library', to: '/answers', icon: AnswersIcon },
    { label: 'Manage resumes', to: '/resumes', icon: ResumeIcon },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Welcome back{user?.firstName ? `, ${user.firstName}` : ''}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your job search at a glance.
        </Typography>
      </Box>

      <ExtensionStatusCard />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {statsError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              action={
                <Button color="inherit" size="small" onClick={load}>
                  Retry
                </Button>
              }
            >
              {statsError}
            </Alert>
          )}

          {stats && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {quickStats.map((stat) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={stat.label}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {stat.value}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {stat.label}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: `${stat.color}.main`, width: 48, height: 48 }}>
                          <stat.icon />
                        </Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Grid container spacing={4}>
            <Grid item xs={12} lg={8}>
              <Stack spacing={3}>
                {stats && (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Funnel
                      </Typography>
                      <Grid container spacing={2}>
                        {[
                          { label: 'Applied last 30 days', value: String(stats.applied_last_30_days) },
                          { label: 'Response rate', value: formatPercent(stats.response_rate) },
                          { label: 'Interview rate', value: formatPercent(stats.interview_rate) },
                          { label: 'Offer rate', value: formatPercent(stats.offer_rate) },
                        ].map((m) => (
                          <Grid item xs={6} sm={3} key={m.label}>
                            <Typography variant="h5" fontWeight={700}>
                              {m.value}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {m.label}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Recent applications
                      </Typography>
                      <Button variant="outlined" size="small" onClick={() => navigate('/applications')}>
                        View all
                      </Button>
                    </Box>

                    {!stats || stats.recent.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No applications yet. Apply to a job with the extension, or save one from the jobs
                        page, and it will show up here.
                      </Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {stats.recent.map((app) => {
                          const meta = statusMeta(app.status);
                          return (
                            <Card
                              key={app.id}
                              variant="outlined"
                              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 2 } }}
                              onClick={() => navigate(`/applications/${app.id}`)}
                            >
                              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                                      {app.job.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                      {app.job.company.name}
                                      {app.applied_at
                                        ? ` | applied ${formatRelative(app.applied_at)}`
                                        : ` | saved ${formatRelative(app.created_at)}`}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={meta.label}
                                    size="small"
                                    sx={{ bgcolor: meta.color, color: 'white', fontWeight: 600 }}
                                  />
                                </Stack>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Stack spacing={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Quick actions
                    </Typography>
                    <Stack spacing={1}>
                      {quickActions.map((action) => (
                        <Button
                          key={action.to}
                          variant="outlined"
                          fullWidth
                          startIcon={<action.icon />}
                          onClick={() => navigate(action.to)}
                          sx={{ justifyContent: 'flex-start' }}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Profile strength
                    </Typography>
                    {snapshotError ? (
                      <Alert severity="warning">{snapshotError}</Alert>
                    ) : strength ? (
                      <>
                        <Box sx={{ mb: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={strengthPct}
                            sx={{ height: 8, borderRadius: 4 }}
                            color={strengthPct >= 85 ? 'success' : strengthPct >= 50 ? 'warning' : 'error'}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {strengthPct}% complete
                        </Typography>
                        <Stack spacing={0.5}>
                          {strength.map((item) => (
                            <Stack key={item.label} direction="row" spacing={1} alignItems="center">
                              <OfferIcon
                                fontSize="small"
                                sx={{ color: item.done ? 'success.main' : 'text.disabled' }}
                              />
                              <Typography
                                variant="body2"
                                color={item.done ? 'text.primary' : 'text.secondary'}
                                sx={{
                                  cursor: item.done ? 'default' : 'pointer',
                                  textDecoration: item.done ? 'none' : 'underline',
                                }}
                                onClick={() => !item.done && navigate(item.to)}
                              >
                                {item.label}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </>
                    ) : null}
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};

export default DashboardPage;
